import * as yup from "yup"
import { Form, Formik } from "formik"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../../../modules/currency/currencyLib"
import { selectEffectiveUserType } from "../../../../../../modules/context/contextSelectors"
import { useAppSelector } from "../../../../../../modules/store/storeHooks"
import { UserType } from "../../../../../../modules/login/loginTypes"
import { ProductWishFormContent } from "./ProductWishFormContent"
import { ProductWishItem } from "./ProductWishDialog"
import { MAX_COUNT_VALUE, MAX_NAME_LENGTH, MAX_PRICE_VALUE } from "../../../../../../constants/inputValidationDefaults"

export type ProductWishFormValues = Readonly<{
    name: string
    quantity?: string
    price?: number
}>

export type ProductWishReadOnlyField = "name" | "quantity"

type ProductWishFormProps = Readonly<{
    onSubmit: (val: ProductWishItem) => void
    initialValues?: ProductWishFormValues
    readOnlyFields?: ReadonlyArray<ProductWishReadOnlyField>
}>

export function ProductWishForm({ onSubmit, initialValues, readOnlyFields }: ProductWishFormProps) {
    const effectiveUserType = useAppSelector(selectEffectiveUserType)
    const isShowPriceField = effectiveUserType !== UserType.USER
    const { l10n } = useAppLocalization()

    const defaultInitialValues: ProductWishFormValues = {
        name: "",
    }

    const validationSchema = yup.object({
        name: yup
            .string()
            .required(l10n.getString("required-field"))
            .max(
                MAX_NAME_LENGTH,
                l10n.getString("create-order-wish-dialog-product-name-max-length", { maxLength: MAX_NAME_LENGTH }),
            ),
        quantity: yup
            .number()
            .required(l10n.getString("required-field"))
            .min(1, l10n.getString("create-order-wish-dialog-quantity-min-value"))
            .max(
                MAX_COUNT_VALUE,
                l10n.getString("create-order-wish-dialog-quantity-max-value", { max: MAX_COUNT_VALUE }),
            ),
        price: yup.number().when([], {
            is: () => isShowPriceField,
            then: (schema) =>
                schema
                    .required(l10n.getString("required-field"))
                    .min(0.01, l10n.getString("create-order-wish-dialog-price-min-value"))
                    .max(
                        MAX_PRICE_VALUE,
                        l10n.getString("create-order-wish-dialog-price-max-value", { max: MAX_PRICE_VALUE }),
                    ),
            otherwise: (schema) => schema.notRequired().nullable(),
        }),
    })

    function handleSubmit(values: ProductWishFormValues) {
        const modifyData = {
            ...values,
            quantity: Number(values.quantity),
            price: values.price ? toMonetaryAmount(toDineroLossy(values.price, toCurrency("DKK"))) : undefined,
        }
        onSubmit(modifyData)
    }

    return (
        <Formik<ProductWishFormValues>
            enableReinitialize
            initialValues={initialValues ?? defaultInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnBlur={false}
        >
            <Form>
                <ProductWishFormContent isShowPriceField={isShowPriceField} readOnlyFields={readOnlyFields} />
            </Form>
        </Formik>
    )
}
